import { Injectable } from '@angular/core';
import { toCanvas } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { SchoolSettings } from './school-settings.service';
import {
  PLAN_DAYS,
  PLAN_ROWS,
  PdfSubjectData,
  WeeklyPlanMeta,
  weekLabel,
} from '../main/weekly-plans/weekly-plans.models';

/**
 * Arabic is never drawn with jsPDF.text(). The browser shapes RTL in a
 * sandboxed iframe, html-to-image snapshots that as pixels, and jsPDF
 * only calls addImage(). Isolation also stops the app dark theme from
 * making cell text white-on-white.
 */
const ARABIC_FONT = '"Noto Naskh Arabic", "Segoe UI", "Tahoma", "Geeza Pro", Arial, sans-serif';
const PDF_PAGE_WIDTH_PX = 1100;
const PDF_SHEET_PADDING_PX = 18;
const LABEL_COL_WIDTH_PX = 110;
const DAY_COL_WIDTH_PX = Math.floor(
  (PDF_PAGE_WIDTH_PX - PDF_SHEET_PADDING_PX * 2 - LABEL_COL_WIDTH_PX) / PLAN_DAYS.length
);
const INK = '#20242b';
const MUTED = '#5b6675';
const ROSE = '#9c2f5b';
const ROSE_DARK = '#7a1f45';
const LINE = '#eccbd9';
const HEAD_BG = '#f3c7dc';
const ROW_BG = '#fbeaf1';

const SHEET_CSS = `
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    color: ${INK};
    font-family: ${ARABIC_FONT};
    direction: rtl;
  }
  #sheet {
    width: ${PDF_PAGE_WIDTH_PX}px;
    padding: 18px;
    background: #ffffff;
    color: ${INK};
    font-family: ${ARABIC_FONT};
    overflow: visible;
  }
  .sheet-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
  }
  .sheet-head-text { flex: 1; text-align: center; }
  .sheet-subject {
    font-weight: 700;
    font-size: 18px;
    color: ${ROSE};
    line-height: 1.4;
  }
  .sheet-school {
    font-size: 13px;
    color: ${MUTED};
    margin-top: 2px;
  }
  .sheet-meta {
    font-size: 13px;
    color: ${MUTED};
    margin-top: 4px;
  }
  .sheet-logos { display: flex; align-items: center; gap: 8px; }
  .sheet-logos img { height: 58px; width: auto; display: block; }
  table.plan {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    border-spacing: 0;
    font-size: 13px;
    color: ${INK};
  }
  table.plan col.col-label { width: ${LABEL_COL_WIDTH_PX}px; }
  table.plan col.col-day { width: ${DAY_COL_WIDTH_PX}px; }
  table.plan th, table.plan td {
    border: 1px solid ${LINE};
    padding: 8px 6px;
    vertical-align: top;
    text-align: center;
    color: ${INK};
    overflow: visible;
    position: relative;
  }
  table.plan thead th {
    background: ${HEAD_BG};
    color: ${ROSE_DARK};
    font-weight: 700;
    font-size: 13px;
    white-space: nowrap;
  }
  table.plan tbody th {
    background: ${ROW_BG};
    color: ${ROSE};
    font-weight: 700;
    width: ${LABEL_COL_WIDTH_PX}px;
    min-width: ${LABEL_COL_WIDTH_PX}px;
    max-width: ${LABEL_COL_WIDTH_PX}px;
    white-space: nowrap;
  }
  table.plan td.cell-data {
    width: ${DAY_COL_WIDTH_PX}px;
    min-width: ${DAY_COL_WIDTH_PX}px;
    max-width: ${DAY_COL_WIDTH_PX}px;
    height: auto;
  }
  table.plan .cell-text {
    display: block;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    text-align: right;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    word-break: break-word;
    min-height: 48px;
    color: ${INK};
    font-size: 13px;
    line-height: 1.55;
    overflow: visible;
  }
  .teacher-line {
    margin-top: 10px;
    font-size: 13px;
    font-weight: 700;
    color: ${ROSE};
    text-align: left;
  }
  .signature-wrap {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    border-top: 1.5px solid ${LINE};
    padding: 24px 0 10px;
  }
  .signature-col { text-align: center; min-width: 280px; }
  .signature-label { font-size: 13px; color: ${MUTED}; margin-bottom: 26px; }
  .signature-name {
    font-size: 15px;
    font-weight: 700;
    color: ${ROSE_DARK};
    border-top: 1px solid #d9e0e8;
    padding-top: 8px;
  }
`;

@Injectable({
  providedIn: 'root'
})
export class WeeklyPlanPdfService {
  private logoDataUrl = '';
  private bannerDataUrl = '';

  private resolveAssetUrl(relativePath: string): string {
    return new URL(relativePath, document.baseURI).href;
  }

  private fmtDate(value: string): string {
    if (!value) {
      return '—';
    }
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString('ar-EG', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  private escapeHtml(value: string): string {
    return (value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private cloneGrid(rows?: string[][]): string[][] {
    return PLAN_ROWS.map((_, rowIndex) =>
      PLAN_DAYS.map((_, dayIndex) => rows?.[rowIndex]?.[dayIndex] ?? '')
    );
  }

  extractGridFromCapture(captureEl: HTMLElement): string[][] {
    const grid = this.cloneGrid();
    captureEl.querySelectorAll('.wp-plan tbody tr').forEach((tr, rowIndex) => {
      tr.querySelectorAll('td textarea').forEach((textarea, dayIndex) => {
        if (grid[rowIndex] && dayIndex < grid[rowIndex].length) {
          grid[rowIndex][dayIndex] = (textarea as HTMLTextAreaElement).value || '';
        }
      });
    });
    return grid;
  }

  private resolveGrid(subject: PdfSubjectData, captureEl?: HTMLElement): string[][] {
    const fromModel = this.cloneGrid(subject.rows);
    if (!captureEl) {
      return fromModel;
    }
    const fromDom = this.extractGridFromCapture(captureEl);
    return PLAN_ROWS.map((_, rowIndex) =>
      PLAN_DAYS.map((_, dayIndex) =>
        fromDom[rowIndex]?.[dayIndex] || fromModel[rowIndex]?.[dayIndex] || ''
      )
    );
  }

  private async toDataUrl(src: string): Promise<string> {
    const response = await fetch(src);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error(`Failed to read image: ${src}`));
      reader.readAsDataURL(blob);
    });
  }

  private async ensureLogos(): Promise<void> {
    if (this.logoDataUrl && this.bannerDataUrl) {
      return;
    }
    const [logo, banner] = await Promise.all([
      this.toDataUrl(this.resolveAssetUrl('assets/weekly-plan/school-logo.png')),
      this.toDataUrl(this.resolveAssetUrl('assets/weekly-plan/year-banner.jpg')),
    ]);
    this.logoDataUrl = logo;
    this.bannerDataUrl = banner;
  }

  private savePdf(pdf: jsPDF, filename: string): void {
    const blob = pdf.output('blob');
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => {
      try {
        window.open(url, '_blank');
      } catch {
        /* ignore popup blockers */
      }
    }, 250);
  }

  private async waitFrames(targetWindow: Window = window): Promise<void> {
    const fonts = targetWindow.document?.fonts;
    if (fonts) {
      try {
        await fonts.load('400 13px "Noto Naskh Arabic"');
        await fonts.load('700 13px "Noto Naskh Arabic"');
        await fonts.ready;
      } catch {
        // Capture still runs if the face is unavailable.
      }
    }
    await new Promise<void>(resolve => {
      targetWindow.requestAnimationFrame(() => {
        targetWindow.requestAnimationFrame(() => resolve());
      });
    });
  }

  private wrapDocument(innerHtml: string): string {
    return `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="utf-8">
  <link rel="stylesheet" href="/assets/fonts/fonts.css">
  <style>${SHEET_CSS}</style>
</head>
<body>${innerHtml}</body>
</html>`;
  }

  private buildWeekHeaderHtml(meta: WeeklyPlanMeta, title: string): string {
    return `
      <div class="sheet-head">
        <div class="sheet-head-text">
          <div class="sheet-subject">${this.escapeHtml(title)}</div>
          <div class="sheet-school">${this.escapeHtml(meta.schoolName || '')} — ${this.escapeHtml(meta.departmentName || '')}</div>
          <div class="sheet-meta">
            ${this.escapeHtml(meta.className || '')} &nbsp;·&nbsp; ${this.escapeHtml(weekLabel(meta.weekOrdinal))} &nbsp;·&nbsp;
            من ${this.fmtDate(meta.startDate)} إلى ${this.fmtDate(meta.endDate)}
          </div>
        </div>
        <div class="sheet-logos">
          <img src="${this.logoDataUrl}" alt="شعار المدرسة">
          <img src="${this.bannerDataUrl}" alt="بالميثاق نبلغ الآفاق">
        </div>
      </div>`;
  }

  private buildPlanTableHtml(grid: string[][], includeDayHeaders = true): string {
    const dayHeaders = PLAN_DAYS.map(day => `<th>${day}</th>`).join('');
    const bodyRows = PLAN_ROWS.map((row, rowIndex) => {
      const cells = (grid[rowIndex] || []).map(text =>
        `<td class="cell-data"><div class="cell-text">${this.escapeHtml(text)}</div></td>`
      ).join('');
      return `<tr><th>${row.label}</th>${cells}</tr>`;
    }).join('');

    return `
      <table class="plan" dir="rtl">
        <colgroup>
          <col class="col-label">
          ${PLAN_DAYS.map(() => '<col class="col-day">').join('')}
        </colgroup>
        ${includeDayHeaders ? `<thead><tr><th>العنصر</th>${dayHeaders}</tr></thead>` : ''}
        <tbody>${bodyRows}</tbody>
      </table>`;
  }

  private buildCompactSubjectHtml(subject: PdfSubjectData, grid: string[][], includeDayHeaders: boolean): string {
    const teacherBit = subject.teacherName
      ? `<span class="subject-teacher"> · معلم المادة: ${this.escapeHtml(subject.teacherName)}</span>`
      : '';

    return `
      <section class="subject-block">
        <div class="subject-title">${this.escapeHtml(subject.subjectName)}${teacherBit}</div>
        ${this.buildPlanTableHtml(grid, includeDayHeaders)}
      </section>`;
  }

  private buildSubjectHtml(meta: WeeklyPlanMeta, subject: PdfSubjectData, grid: string[][]): string {
    const teacherLine = subject.teacherName
      ? `<div class="teacher-line">معلم المادة: ${this.escapeHtml(subject.teacherName)}</div>`
      : '';

    return `
      <div id="sheet">
        ${this.buildWeekHeaderHtml(meta, subject.subjectName)}
        ${this.buildPlanTableHtml(grid, true)}
        ${teacherLine}
      </div>`;
  }

  private buildSignatureHtml(settings: SchoolSettings): string | null {
    const principalName = settings.headName?.trim();
    const sectionHeadName = (settings.sectionHeads || [])
      .map(item => item.name?.trim())
      .find(Boolean) || '';

    if (!principalName && !sectionHeadName) {
      return null;
    }

    const sectionColumn = sectionHeadName ? `
          <div class="signature-col">
            <div class="signature-label">رئيس القسم</div>
            <div class="signature-name">${this.escapeHtml(sectionHeadName)}</div>
          </div>` : '';

    const principalColumn = principalName ? `
          <div class="signature-col">
            <div class="signature-label">مديرة المدرسة</div>
            <div class="signature-name">${this.escapeHtml(principalName)}</div>
          </div>` : '';

    return `
      <div id="sheet">
        <div class="signature-wrap">
          ${sectionColumn}
          ${principalColumn}
        </div>
      </div>`;
  }

  private async rasterizeHtml(innerHtml: string): Promise<HTMLCanvasElement> {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.left = '0';
    iframe.style.top = '0';
    iframe.style.width = `${PDF_PAGE_WIDTH_PX}px`;
    iframe.style.height = '240px';
    iframe.style.opacity = '0.01';
    iframe.style.pointerEvents = 'none';
    iframe.style.border = '0';
    iframe.style.zIndex = '-1';
    iframe.style.background = '#ffffff';

    const loaded = new Promise<void>(resolve => {
      iframe.onload = () => resolve();
    });
    iframe.srcdoc = this.wrapDocument(innerHtml);
    document.body.appendChild(iframe);
    await loaded;

    try {
      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      if (!doc || !win) {
        throw new Error('تعذر تجهيز صفحة التصدير');
      }

      if (doc.fonts?.ready) {
        await doc.fonts.ready;
      }
      await this.waitFrames(win);

      const sheet = doc.getElementById('sheet');
      if (!sheet) {
        throw new Error('تعذر بناء جدول التصدير');
      }

      iframe.style.height = `${Math.max(sheet.scrollHeight + 48, 200)}px`;
      await this.waitFrames(win);

      return await toCanvas(sheet, {
        pixelRatio: 3,
        backgroundColor: '#ffffff',
        cacheBust: true,
        skipFonts: true,
        width: sheet.scrollWidth,
        height: sheet.scrollHeight,
        style: {
          fontFamily: ARABIC_FONT,
          color: INK,
          background: '#ffffff',
        },
      });
    } finally {
      iframe.remove();
    }
  }

  private sliceTallCanvasIntoPdf(canvas: HTMLCanvasElement, pdf: jsPDF, margin: number, pageState: { started: boolean }): void {
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgWidth = pageW - margin * 2;
    const scale = imgWidth / canvas.width;
    const pageHeightPx = (pageH - margin * 2) / scale;

    let position = 0;
    let remaining = canvas.height;
    while (remaining > 0) {
      const sliceH = Math.min(pageHeightPx, remaining);
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = sliceH;
      sliceCanvas.getContext('2d')!.drawImage(canvas, 0, position, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
      const imgData = sliceCanvas.toDataURL('image/png');
      if (pageState.started) {
        pdf.addPage();
      }
      pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, sliceH * scale, undefined, 'NONE');
      pageState.started = true;
      position += sliceH;
      remaining -= sliceH;
    }
  }

  private paginateCanvasToPdf(canvas: HTMLCanvasElement, pdf: jsPDF, margin: number): void {
    this.sliceTallCanvasIntoPdf(canvas, pdf, margin, { started: false });
  }

  async exportSubjectPdf(meta: WeeklyPlanMeta, subject: PdfSubjectData, captureEl?: HTMLElement): Promise<void> {
    await this.ensureLogos();
    const grid = this.resolveGrid(subject, captureEl);
    const canvas = await this.rasterizeHtml(this.buildSubjectHtml(meta, subject, grid));
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    this.paginateCanvasToPdf(canvas, pdf, 20);
    this.savePdf(pdf, `${subject.subjectName}-الأسبوع-${meta.weekOrdinal}.pdf`);
  }

  async exportAllPdf(
    meta: WeeklyPlanMeta,
    subjects: PdfSubjectData[],
    settings: SchoolSettings,
    captureEls?: HTMLElement[],
  ): Promise<void> {
    if (!subjects.length) {
      return;
    }

    await this.ensureLogos();
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const margin = 20;
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const contentW = pageW - margin * 2;
    let cursorY = margin;
    let pageHasContent = false;

    for (let i = 0; i < subjects.length; i++) {
      const subject = subjects[i];
      if (subject.forcePageBreak && pageHasContent) {
        pdf.addPage();
        cursorY = margin;
        pageHasContent = false;
      }

      const grid = this.resolveGrid(subject, captureEls?.[i]);
      const canvas = await this.rasterizeHtml(this.buildSubjectHtml(meta, subject, grid));
      const scale = contentW / canvas.width;
      const imgHeightPts = canvas.height * scale;
      const maxUsableHeight = pageH - margin * 2;

      if (imgHeightPts > maxUsableHeight) {
        if (pageHasContent) {
          pdf.addPage();
        }
        this.sliceTallCanvasIntoPdf(canvas, pdf, margin, { started: true });
        cursorY = margin;
        pageHasContent = false;
        continue;
      }

      if (cursorY + imgHeightPts > pageH - margin) {
        pdf.addPage();
        cursorY = margin;
      }

      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, cursorY, contentW, imgHeightPts, undefined, 'NONE');
      cursorY += imgHeightPts + 14;
      pageHasContent = true;
    }

    const signatureHtml = this.buildSignatureHtml(settings);
    if (signatureHtml) {
      const sigCanvas = await this.rasterizeHtml(signatureHtml);
      const sigScale = contentW / sigCanvas.width;
      const sigHeightPts = sigCanvas.height * sigScale;
      if (cursorY + sigHeightPts > pageH - margin) {
        pdf.addPage();
        cursorY = margin;
      }
      pdf.addImage(sigCanvas.toDataURL('image/png'), 'PNG', margin, cursorY, contentW, sigHeightPts, undefined, 'NONE');
    }

    this.savePdf(pdf, `الخطة-الأسبوعية-الأسبوع-${meta.weekOrdinal}.pdf`);
  }

  /**
   * Opens a browser print dialog with every subject table on a single A4
   * page (landscape or portrait). Shared school/week header appears once;
   * content is scaled to fit without clipping cells.
   */
  async printAllWeek(
    meta: WeeklyPlanMeta,
    subjects: PdfSubjectData[],
    settings: SchoolSettings,
    captureEls?: HTMLElement[],
    orientation: 'landscape' | 'portrait' = 'landscape',
  ): Promise<void> {
    if (!subjects.length) {
      return;
    }

    await this.ensureLogos();

    const subjectBlocks = subjects.map((subject, index) => {
      const grid = this.resolveGrid(subject, captureEls?.[index]);
      return this.buildCompactSubjectHtml(subject, grid, true);
    }).join('\n');

    const signatureHtml = this.buildSignatureHtml(settings);
    const signatureBlock = signatureHtml
      ? `<section class="print-signatures">${signatureHtml}</section>`
      : '';

    const isLandscape = orientation === 'landscape';
    const pageW = isLandscape ? '297mm' : '210mm';
    const pageH = isLandscape ? '210mm' : '297mm';
    const contentW = isLandscape ? '277mm' : '190mm';
    const pageSize = isLandscape ? 'A4 landscape' : 'A4 portrait';
    const labelColW = isLandscape ? '78px' : '70px';

    const printCss = `
      * { box-sizing: border-box; }
      html, body {
        margin: 0;
        padding: 0;
        background: #ffffff;
        color: ${INK};
        font-family: ${ARABIC_FONT};
        direction: rtl;
      }
      #print-frame {
        width: ${pageW};
        height: ${pageH};
        overflow: hidden;
        position: relative;
        background: #ffffff;
      }
      #print-page {
        width: ${contentW};
        padding: 4mm 5mm;
        background: #ffffff;
        position: absolute;
        left: 0;
        top: 0;
        transform-origin: 0 0;
      }
      .sheet-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 4mm;
        padding-bottom: 2mm;
        border-bottom: 1px solid ${LINE};
      }
      .sheet-head-text { flex: 1; text-align: center; }
      .sheet-subject {
        font-weight: 700;
        font-size: 14px;
        color: ${ROSE};
        line-height: 1.3;
      }
      .sheet-school { font-size: 11px; color: ${MUTED}; margin-top: 1px; }
      .sheet-meta { font-size: 10px; color: ${MUTED}; margin-top: 2px; }
      .sheet-logos { display: flex; align-items: center; gap: 6px; }
      .sheet-logos img { height: 36px; width: auto; display: block; }
      .subject-block { margin-bottom: 2.5mm; }
      .subject-title {
        font-size: 11px;
        font-weight: 700;
        color: ${ROSE_DARK};
        margin: 0 0 1mm;
      }
      .subject-teacher { font-weight: 600; color: ${ROSE}; }
      table.plan {
        width: 100%;
        table-layout: fixed;
        border-collapse: collapse;
        font-size: 9px;
        color: ${INK};
      }
      table.plan col.col-label { width: ${labelColW}; }
      table.plan th, table.plan td {
        border: 1px solid ${LINE};
        padding: 2px 3px;
        vertical-align: top;
        text-align: center;
        color: ${INK};
        overflow: visible;
      }
      table.plan thead th {
        background: ${HEAD_BG};
        color: ${ROSE_DARK};
        font-weight: 700;
        font-size: 9px;
        white-space: nowrap;
      }
      table.plan tbody th {
        background: ${ROW_BG};
        color: ${ROSE};
        font-weight: 700;
        white-space: nowrap;
        font-size: 8.5px;
      }
      table.plan .cell-text {
        display: block;
        width: 100%;
        text-align: right;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        word-break: break-word;
        min-height: 0;
        color: ${INK};
        font-size: 8.5px;
        line-height: 1.35;
        overflow: visible;
      }
      .print-signatures { margin-top: 3mm; }
      .print-signatures #sheet { padding: 0; width: auto; }
      .signature-wrap {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        border-top: 1px solid ${LINE};
        padding: 3mm 0 0;
      }
      .signature-col { text-align: center; min-width: 140px; }
      .signature-label { font-size: 9px; color: ${MUTED}; margin-bottom: 8px; }
      .signature-name {
        font-size: 11px;
        font-weight: 700;
        color: ${ROSE_DARK};
        border-top: 1px solid #d9e0e8;
        padding-top: 4px;
      }
      @media print {
        @page { size: ${pageSize}; margin: 0; }
        html, body {
          width: ${pageW};
          height: ${pageH};
          overflow: hidden;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        #print-frame {
          width: ${pageW};
          height: ${pageH};
          overflow: hidden;
        }
      }
    `;

    const html = `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="utf-8">
  <title>الخطة الأسبوعية — ${this.escapeHtml(weekLabel(meta.weekOrdinal))}</title>
  <link rel="stylesheet" href="/assets/fonts/fonts.css">
  <style>${printCss}</style>
</head>
<body>
  <div id="print-frame">
    <div id="print-page">
      ${this.buildWeekHeaderHtml(meta, 'الخطة الأسبوعية')}
      ${subjectBlocks}
      ${signatureBlock}
    </div>
  </div>
  <script>
    function fitToOneA4() {
      var frame = document.getElementById('print-frame');
      var page = document.getElementById('print-page');
      if (!frame || !page) return;
      page.style.transform = 'none';
      var availW = frame.clientWidth || frame.offsetWidth;
      var availH = frame.clientHeight || frame.offsetHeight;
      var contentW = page.scrollWidth;
      var contentH = page.scrollHeight;
      var scale = Math.min(availW / contentW, availH / contentH, 1);
      if (!isFinite(scale) || scale <= 0) scale = 1;
      var offsetX = (availW - contentW * scale) / 2;
      var offsetY = (availH - contentH * scale) / 2;
      page.style.transform = 'translate(' + offsetX + 'px, ' + offsetY + 'px) scale(' + scale + ')';
    }
    window.addEventListener('load', function () {
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () {
          fitToOneA4();
          setTimeout(function () {
            fitToOneA4();
            window.focus();
            window.print();
          }, 200);
        });
      } else {
        setTimeout(function () {
          fitToOneA4();
          window.focus();
          window.print();
        }, 350);
      }
    });
    window.addEventListener('beforeprint', fitToOneA4);
  </script>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('تعذر فتح نافذة الطباعة. اسمح بالنوافذ المنبثقة ثم أعد المحاولة.');
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  }
}
